import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  GenerationSite,
  GenerationSiteListInfo,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { ListGenerationSitesResponse } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'

import { AREA_CODE_OPTIONS } from '~/models/area'
import { type SortOrder, compareTimestamp } from '~/models/tableSort'

/** 発電所 (サイト) 一覧の表示行。未入力の略称・備考は空文字に揃える。 */
export type GenerationSiteRow = {
  id: string
  siteName: string
  siteNameAbbr: string
  areaCode: AreaCode
  note: string
  /** 配下の電源ユニット数 (backend が数える)。 */
  unitCount: number
  /** 配下ユニットのうち、系統コードのエリアが発電所と違うもの (エリア不一致。backend が判定する)。 */
  areaMismatchedUnits: Array<{
    unitName: string
    plantCode: string
    plantAreaCode: AreaCode
  }>
  createdBy: string | undefined
  createdAt: Timestamp | undefined
  updatedBy: string | undefined
  updatedAt: Timestamp | undefined
}

function convertSite(
  site: GenerationSite,
  info: GenerationSiteListInfo | undefined,
): GenerationSiteRow {
  return {
    id: site.id,
    siteName: site.siteName,
    siteNameAbbr: site.siteNameAbbr ?? '',
    areaCode: site.areaCode,
    note: site.note ?? '',
    unitCount: info?.unitCount ?? 0,
    areaMismatchedUnits: (info?.areaMismatchedUnits ?? []).map((unit) => ({
      unitName: unit.unitName,
      plantCode: unit.plantCode,
      plantAreaCode: unit.plantAreaCode,
    })),
    createdBy: site.createdBy,
    createdAt: site.createdAt,
    updatedBy: site.updatedBy,
    updatedAt: site.updatedAt,
  }
}

export function convertListGenerationSitesResponse(
  response: ListGenerationSitesResponse,
): GenerationSiteRow[] {
  // 配下ユニット数・エリア不一致は一覧の応答の付随情報 (infos) にだけ載る。行ごとに id で引く
  const infoById = new Map(
    response.infos.map((info) => [info.generationSiteId, info]),
  )
  return response.items.map((site) => convertSite(site, infoById.get(site.id)))
}

/** エリアの並び順 (北→南、沖縄最後)。一覧にない値は最後。 */
function areaOrder(areaCode: AreaCode): number {
  const index = AREA_CODE_OPTIONS.indexOf(areaCode)
  return index === -1 ? AREA_CODE_OPTIONS.length : index
}

/** 数字の並びを数として比べる名称の比較 (「2号」<「10号」)。 */
const NAME_COLLATOR = new Intl.Collator('ja', { numeric: true })

/** 発電所タブの並べ替えの列。 */
export type SiteSortKey = 'siteName' | 'areaCode' | 'unitCount' | 'updatedAt'
export type SiteSort = { key: SiteSortKey; order: SortOrder }

/** 列見出しを押した時の並び: 別の列は昇順から、同じ列は昇順 ↔ 降順。 */
export function nextSiteSort(
  prev: SiteSort | undefined,
  key: SiteSortKey,
): SiteSort {
  return {
    key,
    order: prev?.key === key && prev.order === 'asc' ? 'desc' : 'asc',
  }
}

/**
 * 一覧に出す行: エリア (UNSPECIFIED = すべて) とキーワード (名称・略称の部分一致、
 * 前後の空白と大文字小文字は無視) で絞り、既定はエリア順 → 名称の自然順に並べる。
 * `sort` を渡すと、その列 (名称 / エリア / 配下ユニット数 / 最終更新日時) で並べる (同じ値は既定順)。
 */
export function filterAndSortGenerationSites(
  rows: readonly GenerationSiteRow[],
  areaCode: AreaCode,
  searchText: string,
  sort?: SiteSort,
): GenerationSiteRow[] {
  const keyword = searchText.trim().toLowerCase()
  const byDefault = (a: GenerationSiteRow, b: GenerationSiteRow): number =>
    areaOrder(a.areaCode) - areaOrder(b.areaCode) ||
    NAME_COLLATOR.compare(a.siteName, b.siteName)
  const direction = sort?.order === 'desc' ? -1 : 1
  const compareBy = (a: GenerationSiteRow, b: GenerationSiteRow): number => {
    switch (sort?.key) {
      case 'siteName':
        return NAME_COLLATOR.compare(a.siteName, b.siteName)
      case 'areaCode':
        return areaOrder(a.areaCode) - areaOrder(b.areaCode)
      case 'unitCount':
        return a.unitCount - b.unitCount
      case 'updatedAt':
        if (a.updatedAt === undefined) return b.updatedAt === undefined ? 0 : 1
        if (b.updatedAt === undefined) return -1
        return compareTimestamp(a.updatedAt, b.updatedAt)
      default:
        return 0
    }
  }
  return rows
    .filter((row) => {
      if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
        return false
      }
      if (keyword === '') {
        return true
      }
      return (
        row.siteName.toLowerCase().includes(keyword) ||
        row.siteNameAbbr.toLowerCase().includes(keyword)
      )
    })
    .sort((a, b) => direction * compareBy(a, b) || byDefault(a, b))
}
